<template>
  <div>
    <!-- section pour visualisation un lien de référence -->
    <a
      v-if="referenceType"
      :aria-label="displayValue ? displayValue : value"
      class="button inTable"
      tabindex="0"
      @click="openReferenceDetail()"
      @keyup.enter="openReferenceDetail()"
      @keyup.space="openReferenceDetail()"
    >
      {{ displayValue ? displayValue : value }}
      <LoadingAnimate
        v-if="isLoading"
        :size="'is-small'"
      />
    </a>
    <p
      v-else-if="column && column.datePattern"
      :aria-label="column.formatDateValue(value)"
    >
      {{ value.length > 0 ? column.formatDateValue(value) : value }}
    </p>
    <p
      v-else-if="patternCheckerDate"
      :aria-label="datePatternLang(value, patternCheckerDate)"
    >
      {{ value.length > 0 ? datePatternLang(value, patternCheckerDate) : value }}
    </p>
    <p
      v-else-if="typeof value === 'string' && value.indexOf('date') === 0"
      :aria-label="datePatternLang(value)"
    >
      {{ datePatternLang(value) }}
    </p>
    <p
      v-else
      :aria-label="value"
      class="column"
    >
      <!--
        On préfère displayValue ( si fourni par le parent ) à value
        pour autoriser le parent à passer une chaîne tronquée pour
        l'affichage ( ex: cellule de tableau capée à 150 chars + "..."
        cf. useCellExpand.cellDisplayValue ). Le fallback sur value
        garde le comportement legacy quand display-value n'est pas
        passé.
      -->
      {{ displayValue ? displayValue : value }}
    </p>

    <!-- modal de visualisation d'une donnée de référence -->
    <b-modal
      v-model="isCardModalActive"
      custom-class="referenceDetails"
    >
      <div class="card">
        <header class="card-header is-align-content-center">
          <p
            :aria-label="reference
              ? reference.localName !== value
                ? getValueDisplay(reference.rows[0], application.configuration.applicationDescription.defaultLanguage)
                : displayValue
              : value"
            class="card-header-title cardHeaderTitle"
            field="name"
          >
            {{
              reference
                ? reference.localName !== value
                  ? getValueDisplay(reference.rows[0], application.configuration.applicationDescription.defaultLanguage)
                  : displayValue
                : value
            }}
          </p>
        </header>
        <div class="card-content">
          <div class="content is-align-content-center">
            <b-table
              :data="refValuesObj.data"
              :sticky-header="false"
            >
              <b-table-column
                v-for="column in refValuesObj.columns"
                :key="column.id"
                v-slot="props"
                :aria-label="column.label"
                :label="column.label"
              >
                <span>
                  <div
                    v-if="typeof props.row[column.field] == 'boolean'"
                    :aria-label="props.row[column.field] ? $t('boolean.true') : $t('boolean.false')"
                  >
                    {{ props.row[column.field] ? $t("boolean.true") : $t("boolean.false") }}
                  </div>
                  <DatasManyLink
                    v-else-if="
                      Array.isArray(props.row[column.field]) && props.row[column.field].length !== 0
                    "
                    :application="application"
                    :column-id="column.id"
                    :info-values="props.row[column.field]"
                    :loaded-references-by-key="{}"
                    :multiplicity="true"
                    :reference-type="searchRefLinkedTo(props.row['colonne'])"
                  />
                  <DatasLink
                    v-else-if="
                      column.field === 'valeur' &&
                        props.row.refType &&
                        props.row.refType !== '' &&
                        props.row.refType !== null &&
                        props.row.description?.reference === true
                    "
                    :application="application"
                    :display-value="props.row[column.field]"
                    :loaded-references-by-key="{}"
                    :reference-type="props.row.refType"
                    :value="props.row.valeur"
                  />
                  <p
                    v-else-if="column.field === 'colonne'"
                    :aria-label="getColumnNameView(props.row[column.field], application, referenceType, column)"
                  >
                    {{
                      getColumnNameView(props.row[column.field], application, referenceType, column)
                    }}
                  </p>
                  <p
                    v-else-if="column.field === 'valeur' && props.row.description?.type === 'DateType'"
                    :aria-label="formatModalDateValue(props.row[column.field], props.row.description)"
                  >
                    {{ formatModalDateValue(props.row[column.field], props.row.description) }}
                  </p>
                  <p
                    v-else-if="column.field === 'valeur' && typeof props.row[column.field] === 'string' && props.row[column.field].indexOf('date') === 0"
                    :aria-label="datePatternLang(props.row[column.field])"
                  >
                    {{ datePatternLang(props.row[column.field]) }}
                  </p>
                  <p v-else>
                    {{ props.row[column.field] }}
                  </p>
                </span>
              </b-table-column>
            </b-table>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { defineOptions, provide, ref } from "vue";
import DatasManyLink from "@/components/datas/DatasManyLink.vue";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import useBoolean from "@/composable/components/boolean";
import useObject from "@/composable/components/object";
import { datePatternLang } from "@/composable/application/datePattern";
import { dataLoader } from "@/composable/data/dataLoader";
import services from "@/composable/services";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import { BModal, BTable, BTableColumn } from "buefy";
import { TableColumn } from "@/types/ui/TableColumn";
import { Component } from "@/types/front/LocalComponent";
import i18n from "@/i18n";
defineOptions({ name: "DatasLink" });
const props = defineProps<{
  patternCheckerDate?: string;
  component?: object;
  column?: Component;
  application: LocalApplicationResult;
  referenceType: string;
  value: string;
  displayValue?: string;
  infoValues?: object | unknown;
  loadedReferencesByKey?: object;
  row?: object;
  columnId?: string;
}>();

const emit = defineEmits<{
  (e: "changedRefValues", payload: any): void;
}>();

const loader = dataLoader(services);
provide("reference:dataLoader", loader);

const { getValueDisplay, getOrLoadDataByNaturalKey, getColumnNameView } = loader;
const columns: TableColumn[] = [
  { id: "column", field: "colonne", label: i18n.global.t("table.column-column"), type: "column" },
  { id: "value", field: "valeur", label: i18n.global.t("table.column-value"), type: "value" },
];
const { reactiveObject: refValuesObj, doChangeObject: changeRefValuesObj }
  = useObject({
    active: false,
    localName: props.value,
    data: [],
    columns: columns,
  });

const isCardModalActive = useBoolean().refBoolean;
const { refBoolean: isLoading, doChangeBoolean: changeIsLoading } = useBoolean();
const reference = ref<any>();

const formatModalDateValue = (value: any, description: any): string => {
  if (!value) return String(value || "");

  // Si c'est une string qui commence par "date", utiliser datePatternLang
  if (typeof value === "string" && value.indexOf("date") === 0) {
    return datePatternLang(value);
  }

  // Si on a un pattern dans le checker, l'utiliser
  const pattern = description?.checker?.params?.pattern;
  if (pattern) {
    return datePatternLang(value, pattern);
  }

  return datePatternLang(value);
};

async function getOrLoadReference() {
  if (props.referenceType && (props.value || props.columnId)) {
    const result = await getOrLoadDataByNaturalKey(
      props.application,
      props.referenceType,
      props.value,
    );

    // Bug fix : si le backend renvoie 400 / 5xx ou si la natural key
    // n'existe pas , getOrLoadDataByNaturalKey retourne null. On
    // signale silencieusement ( reference.value reste à undefined )
    // pour ne pas crasher avec "Cannot set properties of null
    // (setting 'localName')" plus bas. openReferenceDetail vérifie
    // reference.value avant d'aller plus loin.
    if (!result) {
      reference.value = null;
      changeIsLoading(false);
      return;
    }

    if (result?.rows?.length) {
      result.refValues = {
        referenceTypeForReferencingColumns:
        result.referenceTypeForReferencingColumns,
        refValue: result.rows[0].values,
      };
    }

    reference.value = result;
    reference.value.localName = props.value;
  }
}

async function openReferenceDetail() {
  changeIsLoading(true);
  await getOrLoadReference();
  // Si le chargement a échoué ( ex: 400 backend , clé absente ) , on
  // reste sur la page courante au lieu d'ouvrir une modal vide qui
  // crasherait sur reference.value.refValues.
  if (!reference.value) {
    changeIsLoading(false);
    return;
  }

  const refValues = reference.value.refValues;

  const data = Object.entries(refValues.refValue)
    .filter(([k]) => !k.startsWith("__display_"))
    .map(([colonne, valeur]) => {
      const componentDesc = props.application.configuration.dataDescription?.[props.referenceType]
        ?.componentDescriptions?.[colonne];

      return {
        colonne,
        valeur,
        description: componentDesc,
        refType: componentDesc?.referenceCheckerType || null,
      };
    });

  const result = {
    data,
    columns: columns,
    active: true,
    reference: props.referenceType,
  };

  isCardModalActive.value = true;
  changeRefValuesObj({
    ...refValues,
    ...result,
    localRefName: reference.value.localRefName,
  });

  emit("changedRefValues", {
    key: props.value,
    refType: props.referenceType,
    refValuesObj,
    refValue: (refValuesObj as any).refValue,
  });

  changeIsLoading(false);
}

function searchRefLinkedTo(columnName: string): string {
  const dataDesc
    = props.application.configuration.dataDescription?.[props.referenceType];
  if (!dataDesc) return "";
  if (dataDesc.depends.length !== 0) {
    return (dataDesc.depends[0] as any).component === columnName
      ? (dataDesc.depends[0] as any).references
      : "";
  }
  return "";
}

defineExpose({
  reference,
  refValuesObj,
});
</script>

<style scoped lang="scss">
.button.inTable {
  color: $primary-dark;
  background-color: transparent;
  border: transparent;
}

.button.inTable:hover {
  color: $primary !important;
  text-decoration: underline !important;
}
</style>
